// Hook que invoca la RPC `registrar_dolor_sesion` (migration 0006) para
// persistir el nivel de dolor reportado por el paciente al confirmar el
// cierre de sesión en EscalaDolor.
//
// Decisión arquitectónica (Opción β) en docs/decisions/pain-indicator-mvp.md:
// SEPARADA de `guardar_sesion_completada` para que las responsabilidades
// sean distintas. Si el paciente cierra la app antes de confirmar, la
// sesión queda con dolor_reportado=NULL — semántica explícita "no
// reportado", no "perdido en transacción".
//
// Patrón idéntico a useGuardarSesionCompletada: mutate(input) =>
// Promise<void> + state { loading, error, success }. El caller (padre
// de EscalaDolor) usa `loading` para disabled del botón y `error` para
// mostrar mensaje de reintentar.

import { useState } from "react";
import { supabase } from "../client";

export interface RegistrarDolorInput {
  sesion_id: string;
  /** Nivel 1..10. Validación blanda en el cliente (componente acepta
   *  solo 1-10 vía slider/buttons). Validación dura en la RPC. */
  dolor: number;
}

export interface UseRegistrarDolorReturn {
  mutate: (input: RegistrarDolorInput) => Promise<void>;
  loading: boolean;
  error: Error | null;
  success: boolean;
  /** Reset manual del error después de un retry. El componente lo
   *  invoca si el paciente vuelve a tocar el slider tras un fallo. */
  resetError: () => void;
}

export function useRegistrarDolor(): UseRegistrarDolorReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  async function mutate(input: RegistrarDolorInput) {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const { error: rpcError } = await supabase.rpc(
        "registrar_dolor_sesion",
        {
          p_sesion_id: input.sesion_id,
          p_dolor: input.dolor,
        },
      );
      if (rpcError) throw rpcError;
      setSuccess(true);
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      console.error("[useRegistrarDolor] failed:", err);
    } finally {
      setLoading(false);
    }
  }

  function resetError() {
    setError(null);
  }

  return { mutate, loading, error, success, resetError };
}
